// source-line-block.ts — shared render of an attributed "source lines + total"
// list-group block, used by every info panel that shows a {total, sources}
// computed cluster (essence, AC modifiers, damage mitigation, …).
//
// Direct port of render_integer_layer_sources_breakdown_block()
// (includes/functions.php:6565): one list-group-item per source line (icon +
// optional item.php link + signed/braced badge), then a "Total" footer row.

import type { CachedCaster, SourceRow } from './types';
import { catalogShell, itemsCol, inventoryField, primaryPictureOf } from '../shared/array-types';
import { findFullNestedItem, findInventoryInstanceNode } from '../object-card/find-cached-item';
import { statusRunes, statusRuneMountHtml, statusIconRegistered } from '../shared/status-rune';

// Item types whose source lines render as a runic sigil (when the href resolves to an
// unregistered, layer-bearing def): the spell + AbilityType family, plus status effects.
// Mirrors ft_rune_icon_eligible() (ft_rune_icon_mount_inc.php) widened with 'status_effect'
// (status-rune.ts already runes non-standard statuses). Everything else — feats
// (feat_essence_bonus), backgrounds, ancestries without a registered PNG — falls back to
// iconMap(kind). Registered catalog icons (standard statuses, ancestries) win before this.
const RUNE_LINE_KINDS = new Set<string>([
    'spell', 'status_effect',
    'full_ability', 'ability', 'attack_ability', 'quick_ability', 'free_ability',
    'reaction_ability', 'opportunity_attack_ability', 'passive_ability',
    'curse', 'menta', 'sanguine',
]);

function sourceRecord(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

/**
 * Pick the icon for one attributed source line, keyed off the line's OWN href_id rather than
 * the generic `kind` slug:
 *   1. href resolves to a REGISTERED catalog icon → that bespoke PNG (e.g. the actual
 *      Encumbered status icon, a creature_sub_type ancestry icon) — a free swap, no mount.
 *   2. href resolves to an unregistered, layer-bearing ability/spell/effect def → its L0 rune
 *      sigil (a .ft-rune-mount placeholder; the caller's ftMountRuneIcons() hydrates it).
 *   3. otherwise → iconMap(kind) (feats/backgrounds/unresolvable hrefs keep today's behaviour).
 * Returns the icon HTML and whether it is an (interactive) rune mount so the wrapper can drop
 * aria-hidden for that case.
 */
function lineIcon(caster: CachedCaster | undefined, href: string, kind: string): { html: string; rune: boolean } {
    const isUuid = href !== '' && checkIfValidUUID(href);
    if (isUuid && statusIconRegistered(href)) {
        return { html: iconMap(href, 22, 22), rune: false };
    }
    const def = (isUuid && caster) ? sourceRecord(catalogShell(caster, href)) : null;
    if (def && RUNE_LINE_KINDS.has(kind) && statusRunes(href, def)) {
        return { html: statusRuneMountHtml(def), rune: true };
    }
    return { html: iconMap(kind, 22, 22), rune: false };
}

/**
 * ROUND-6 #3 (2026-06-12): stored provenance source rows may carry NO `label` — the store
 * point (ft_lean_compute_source_rows) proved it round-trips from href_id. Re-derive through
 * the same chain the PHP proof used (ft_resolve_breakdown_href_name): active status row by
 * status_id → catalog identity → full in-blob copy. Moved here from skill-adv-dis-info.ts
 * (Round-3 #4) so every sources-block panel shares it.
 */
export function resolveLineLabel(caster: CachedCaster, href: string): string {
    if (!href) return '';
    const want = href.toUpperCase();
    for (const mode of ['target', 'caster']) {
        const rows = caster.statuses?.[mode];
        const list = Array.isArray(rows) ? rows : (rows && typeof rows === 'object' ? Object.values(rows) : []);
        for (const row of list) {
            const rowRecord = sourceRecord(row);
            const sidRaw = rowRecord?.status_id;
            const sid = Array.isArray(sidRaw) ? sidRaw[0] : sidRaw;
            if (sid && String(sid).toUpperCase() === want) {
                const nm = itemsCol(row, 'item_name') ?? itemsCol(rowRecord?.status_arr, 'item_name');
                if (nm) return String(nm);
            }
        }
    }
    const shell = sourceRecord(catalogShell(caster, href));
    const shellItems = sourceRecord(shell?.items);
    const shellName = shell
        ? (typeof shell.item_name === 'string' && shell.item_name !== '' ? shell.item_name : shellItems?.item_name)
        : undefined;
    if (typeof shellName === 'string' && shellName !== '') return shellName;
    const full = findFullNestedItem(href);
    if (full) {
        const nm = itemsCol(full, 'item_name');
        if (nm) return String(nm);
    }
    return '';
}

/**
 * ROUND-9 #5: stored provenance rows may carry NO `kind` — the store point
 * (ft_lean_compute_source_rows / ft_lean_adv_dis_lines) proved it equals the catalog item_type
 * of href_id. Re-derive through the same chain (root catalog identity → full in-blob copy).
 * The equality proof at store time guarantees the re-derived string — and therefore the
 * iconMap key — is byte-identical to what was dropped. Returns '' when unresolvable.
 */
export function resolveLineKind(caster: CachedCaster | undefined, row: { kind?: string; href_id?: string }): string {
    if (typeof row.kind === 'string' && row.kind !== '') return row.kind;
    const href = String(row.href_id ?? '');
    if (!href || !caster) return '';
    const shell = sourceRecord(catalogShell(caster, href));
    const shellItems = sourceRecord(shell?.items);
    const shellType = shell
        ? (typeof shell.item_type === 'string' && shell.item_type !== '' ? shell.item_type : shellItems?.item_type)
        : undefined;
    if (typeof shellType === 'string' && shellType !== '') return shellType;
    const full = findFullNestedItem(href);
    if (full) {
        const t = itemsCol(full, 'item_type');
        if (t) return String(t);
    }
    return '';
}

// ── PROVENANCE TREE (2026-06-15) ──────────────────────────────────────────────
// A source line names the item that CONTRIBUTES (e.g. the passive "Light Pierces
// All"), but not where that item comes FROM. Resolve the granting chain back toward
// the host — exactly how the Passive Abilities tab groups (each ability entry in
// computed.creature.abilities carries a `source`; subclass→class is derivable from
// player_characters.class_N / subclass_N) — and group lines under that trail so the
// breakdown reads as a tree: «Paladin › Oath of Purification» → Light Pierces All.
// Pure client-side off the cached blob; no backend change.

interface ProvCrumb { id: string; name: string; type: string; pic?: string; }

function provUuid(v: unknown): string {
    const s = Array.isArray(v) ? v[0] : v;
    return typeof s === 'string' ? s : '';
}

/** name + type for any item uuid, reusing the tested label/kind resolvers (catalog
 *  shell → full nested → status rows). ftItemNameById walks EVERY cache catalog, so it
 *  resolves catalogized class/subclass defs thinned out of the single creature blob. */
function provIdentity(caster: CachedCaster, id: string): { name: string; type: string } {
    let name = '';
    try { name = (typeof ftItemNameById === 'function' ? ftItemNameById(id) : '') || ''; } catch { name = ''; }
    if (name === '') name = resolveLineLabel(caster, id);
    const type = resolveLineKind(caster, { href_id: id }) || '';
    return { name, type };
}

/** The `source` uuid recorded on the ability entry whose id is `abilityId`, scanning the
 *  computed.creature.abilities buckets (lean rows: {item_id|ability_id, source, …}). '' when
 *  not an ability / no source (carrier / inventory / host contributions have none). */
function abilityEntrySource(caster: CachedCaster, abilityId: string): string {
    const buckets = caster.computed?.creature?.abilities;
    if (!buckets || typeof buckets !== 'object') return '';
    const want = abilityId.toLowerCase();
    for (const type of Object.keys(buckets)) {
        const list = buckets[type];
        const arr = Array.isArray(list) ? list : (list && typeof list === 'object' ? Object.values(list) : []);
        for (const entry of arr) {
            const e = sourceRecord(entry);
            if (!e) continue;
            const id = provUuid(e.item_id) || provUuid(e.ability_id);
            if (id && id.toLowerCase() === want) return provUuid(e.source);
        }
    }
    return '';
}

/** The owning class uuid for a subclass (class_N / subclass_N share a slot), or ''. */
function classForSubclass(caster: CachedCaster, subclassId: string): string {
    const pc = caster.player_characters;
    if (!pc || typeof pc !== 'object') return '';
    const k = subclassId.toLowerCase();
    for (let x = 1; x <= 20; x++) {
        const sid = provUuid(pc['subclass_' + x]);
        if (sid && sid.toLowerCase() === k) return provUuid(pc['class_' + x]);
    }
    return '';
}

/** Display identity (name + icon type) for an equipped inventory INSTANCE node. The crumb id is
 *  an inventory_id, NOT an item_id — so the item_id-keyed resolvers (ftItemNameById / catalogShell /
 *  findFullNestedItem) can't name it. Read the instance's `inventory_name` override directly; for a
 *  THINNED instance (no `items` bucket) resolve the template by inventory_item_id for the name/type
 *  fallbacks so iconMap gets the item's real family glyph. */
function inventoryCrumbIdentity(invNode: unknown): { name: string; type: string } {
    const invName = inventoryField<string>(invNode, 'inventory_name');
    let name = (typeof invName === 'string' && invName !== '') ? invName : '';
    let type = String(itemsCol(invNode, 'item_type') ?? '');
    if (name === '' || type === '') {
        const tplId = String(inventoryField<string>(invNode, 'inventory_item_id') ?? '');
        const tpl = (tplId !== '' && checkIfValidUUID(tplId)) ? findFullNestedItem(tplId) : null;
        if (name === '') name = String(itemsCol(invNode, 'item_name') ?? (tpl ? itemsCol(tpl, 'item_name') : '') ?? '');
        if (type === '' && tpl) type = String(itemsCol(tpl, 'item_type') ?? '');
    }
    return { name: name || 'Equipped item', type: type || 'inventory_equipped' };
}

/** The item-art thumbnail URL for an equipped inventory crumb: the instance's own uploaded art
 *  first, else the template def's picture (resolved by inventory_item_id — a thinned instance
 *  carries no pictures of its own). '' when the item has no art (caller falls back to a glyph). */
function inventoryCrumbPicture(invNode: unknown): string {
    let pic = primaryPictureOf(invNode);
    if (!pic) {
        const tplId = String(inventoryField<string>(invNode, 'inventory_item_id') ?? '');
        const tpl = (tplId !== '' && checkIfValidUUID(tplId)) ? findFullNestedItem(tplId) : null;
        if (tpl) pic = primaryPictureOf(tpl);
    }
    const url = pic && pic.picture_url;
    return url ? String(url) : '';
}

/** When ability `abilityId` is granted by an enchantment ON inventory node `invNode`, the
 *  enchantment's crumb (its template id + the composite enchantment name). The relocated
 *  caster-level ability carries `source = inventory_id` (the enchantment_template_id is leaned
 *  off), so the enchantment itself is recovered from the instance's computed.enchantment block,
 *  matching the ability by id. Null when the contribution is a NATIVE item layer (not an
 *  enchantment) — the inventory crumb alone then names it. */
function enchantmentCrumbFor(invNode: unknown, abilityId: string): { id: string; name: string } | null {
    const node = sourceRecord(invNode);
    const computed = sourceRecord(node?.computed);
    const ench = computed?.enchantment;
    const blocks = Array.isArray(ench) ? ench : (ench && typeof ench === 'object' ? [ench] : []);
    const want = abilityId.toLowerCase();
    for (const e of blocks) {
        const enchantment = sourceRecord(e);
        const abilities = sourceRecord(enchantment?.abilities);
        if (!abilities || typeof abilities !== 'object') continue;
        for (const bucket of Object.values(abilities)) {
            const arr = Array.isArray(bucket) ? bucket : [];
            for (const rawEntry of arr) {
                const entry = sourceRecord(rawEntry);
                const eid = provUuid(entry?.item_id) || provUuid(entry?.ability_id);
                if (eid && eid.toLowerCase() === want) {
                    const rawName = enchantment?.item_name;
                    const nm = Array.isArray(rawName) ? rawName[0] : rawName;
                    return { id: provUuid(enchantment?.enchantment_template_id), name: String(nm ?? '') };
                }
            }
        }
    }
    return null;
}

/** Ancestor crumbs for a contributing item, deepest-first → host (the item itself is the
 *  ROW, not a crumb). Walks `source` up the chain (passive→passive→subclass), and for a
 *  subclass source prepends its owning class. An inventory_id source terminates the chain at
 *  the equipped ITEM — «Item › Enchantment» when the contribution is enchantment-granted.
 *  Empty for host/carrier/ancestry contributions (their line label already names the
 *  source — they render flush). */
function ancestorTrail(caster: CachedCaster, href: string): ProvCrumb[] {
    const crumbs: ProvCrumb[] = [];
    const seen = new Set<string>();
    let node = String(href || '');
    let guard = 0;
    while (node !== '' && checkIfValidUUID(node) && guard++ < 12) {
        const src = abilityEntrySource(caster, node);
        if (src === '' || !checkIfValidUUID(src)) break;
        const lk = src.toLowerCase();
        if (lk === node.toLowerCase() || seen.has(lk)) break; // self / cycle guard
        seen.add(lk);

        // INVENTORY ROOT: an equipped item's id terminates the chain (items carry no `source`).
        // The item_id-keyed provIdentity can't name an inventory_id, so resolve the instance node
        // for the item name; when the layer comes from an enchantment, insert the enchantment crumb
        // between the item and the row. Order is outermost-first → «Item › Enchantment».
        const invNode = findInventoryInstanceNode(src);
        if (invNode) {
            const ench = enchantmentCrumbFor(invNode, node);
            if (ench && checkIfValidUUID(ench.id)) {
                crumbs.unshift({ id: ench.id, name: ench.name, type: 'enchantment_template' });
            }
            const ident = inventoryCrumbIdentity(invNode);
            crumbs.unshift({ id: src, name: ident.name, type: ident.type, pic: inventoryCrumbPicture(invNode) });
            break;
        }

        const id = provIdentity(caster, src);
        crumbs.unshift({ id: src, name: id.name, type: id.type });
        if (id.type === 'subclass') {
            const cls = classForSubclass(caster, src);
            if (cls !== '' && !seen.has(cls.toLowerCase())) {
                seen.add(cls.toLowerCase());
                const ci = provIdentity(caster, cls);
                crumbs.unshift({ id: cls, name: ci.name, type: ci.type });
            }
        }
        node = src;
    }
    return crumbs;
}

/** One contributing source row: kind icon + name (the name itself drills in via openItemInfo,
 *  in-panel) + the signed/override badge (green = positive, red = negative). */
function renderSourceRow(ln: SourceRow, badgeClass: string, caster: CachedCaster | undefined, indented: boolean): string {
    // ROUND-9 #5: `kind` may be elided at the store point — re-derive from the catalog.
    const kind = resolveLineKind(caster, ln) || 'passive_ability';
    const value = Number(ln.value ?? 0);
    // {x} is a MASTER OVERRIDE — the source sets the layer to x rather than adding to it
    // (the compiled total already reflects the override). Render it distinctly so it doesn't
    // read as a "+x" contribution. Applies anywhere a source carries value_brace.
    const isOverride = (ln.value_brace !== undefined && ln.value_brace !== null);
    const badge = isOverride ? `{${Number(ln.value_brace)}}` : `${value >= 0 ? '+' : ''}${value}`;
    // Sign-coloured contribution badge (override keeps its amber "sets-this-value" tint). The
    // caller's badgeClass is intentionally NOT used for the per-line delta — a +N/−N reads
    // clearest as green/red regardless of which panel it came from (essence, AC, movement, …).
    const badgeCls = isOverride
        ? 'bg-warning text-dark'
        : `ft-bd-badge ft-bd-badge-${value < 0 ? 'neg' : 'pos'}`;
    const overrideTitle = isOverride ? ' title="Master override — sets this value (does not add)" data-bs-toggle="tooltip"' : '';

    const href = String(ln.href_id ?? '');
    const valid = href !== '' && checkIfValidUUID(href);
    const label = escapeHtml(String(ln.label ?? (caster ? resolveLineLabel(caster, href) : '')));
    // The name IS the drill-in: clicking it opens the contributing item's own panel in place
    // (openItemInfo — cache-first, no DB ping when the item is full-nested in the caster blob).
    // The item.php href is kept so ctrl/middle-click still opens the full page in a new tab.
    const labelHtml = valid
        ? `<a class="link-primary ft-bd-label" href="${escapeHtml(BASE_URL + 'item.php?id=' + href)}" onclick="ftDrillFromBreakdown('${escapeHtml(href)}'); return false;">${label}</a>`
        : label;

    // Icon keyed off this line's OWN href (real status/ancestry PNG, or a rune sigil for an
    // ability/spell/effect), falling back to the kind slug — see lineIcon().
    const icon = lineIcon(caster, href, kind);

    return `
        <div class="list-group-item d-flex justify-content-between align-items-center gap-2 flex-wrap py-2 w-100${indented ? ' ft-bd-row-indent' : ''}">
            <span class="d-flex align-items-center gap-2 text-start flex-grow-1 min-w-0">
                <span class="flex-shrink-0 d-inline-flex align-items-center"${icon.rune ? '' : ' aria-hidden="true"'}>${icon.html}</span>
                <span class="min-w-0">${labelHtml}</span>
            </span>
            <span class="d-flex align-items-center gap-2 flex-shrink-0">
                <span class="badge ${badgeCls}"${overrideTitle}>${escapeHtml(badge)}</span>
            </span>
        </div>`;
}

/** The granting-source band (a group header): the provenance crumb trail with › arrows,
 *  each crumb clickable (drills in via ftDrillFromBreakdown so Back returns to this breakdown),
 *  the last crumb bold. Wrapped in <summary> so the <details> gives free, JS-free, repaint-proof collapse. */
function renderCrumbBand(crumbs: ProvCrumb[]): string {
    const trail = crumbs.map((c, i) => {
        const isLast = i === crumbs.length - 1;
        // The crumb's OWN art: a resolved item thumbnail (equipped inventory → its picture) wins,
        // then the crumb id's registered icon (the Paladin / Oath of Purification PNG), then the
        // generic type glyph (else 'host'). statusIconRegistered = "has a real PNG".
        const icon = c.pic
            ? `<img src="${escapeHtml(c.pic)}" alt="" width="16" height="16" class="rounded border border-secondary" style="width:16px;height:16px;object-fit:cover;background-color:#1b1f24;" loading="lazy">`
            : (checkIfValidUUID(c.id) && statusIconRegistered(c.id))
                ? iconMap(c.id, 16, 16)
                : iconMap(c.type || 'host', 16, 16);
        const nm = escapeHtml(c.name || c.type || 'Source');
        // Drill in via ftDrillFromBreakdown (NOT bare openItemInfo) so the top-bar Back arrow
        // returns to this breakdown — same path as the source-row names.
        const click = checkIfValidUUID(c.id) ? ` role="button" onclick="ftDrillFromBreakdown('${escapeHtml(c.id)}');"` : '';
        return `<span class="ft-bd-crumb${isLast ? ' fw-semibold' : ''}"${click}>${icon}<span>${nm}</span></span>`;
    }).join('<span class="ft-bd-crumb-arrow" aria-hidden="true">›</span>');
    return `<summary class="list-group-item ft-bd-group-summary d-flex align-items-center gap-2 py-2 w-100">
        <span class="ft-bd-chevron flex-shrink-0" aria-hidden="true">▾</span>
        <span class="d-flex align-items-center gap-1 flex-wrap min-w-0">${trail}</span>
    </summary>`;
}

/**
 * @param lines               attributed source rows ({label?,value,href_id,kind,value_brace?})
 * @param totalFooterIconSlug icon slug for the footer "Total" row
 * @param rolledTotal         the authoritative total (footer badge)
 * @param badgeClass          Bootstrap badge classes for line + footer badges
 * @param caster              blob root for re-deriving leaned labels (ROUND-6 #3) AND resolving
 *                            the provenance tree; rows with an embedded label render it unchanged
 */
export function renderSourceLineBlock(
    lines: SourceRow[],
    totalFooterIconSlug: string,
    rolledTotal: number,
    badgeClass = 'bg-secondary',
    caster?: CachedCaster,
): string {
    // Partition: contributions WITH a granting chain (passive/sub-feature) group under a
    // crumb band; direct contributions (host / carrier / inventory — trail empty) render flush
    // in their original (compute) order, which already leads with host/carrier. Flush-first
    // then bands keeps it readable without threading interleave logic.
    let flushRows = '';
    const groups = new Map<string, { crumbs: ProvCrumb[]; rows: string }>();
    const order: string[] = [];
    for (const ln of lines) {
        const href = String(ln.href_id ?? '');
        const trail = caster ? ancestorTrail(caster, href) : [];
        if (trail.length === 0) {
            flushRows += renderSourceRow(ln, badgeClass, caster, false);
            continue;
        }
        const key = trail.map(c => c.id.toLowerCase()).join('>');
        let g = groups.get(key);
        if (!g) { g = { crumbs: trail, rows: '' }; groups.set(key, g); order.push(key); }
        g.rows += renderSourceRow(ln, badgeClass, caster, true);
    }

    let bands = '';
    for (const key of order) {
        const g = groups.get(key)!;
        bands += `<details class="ft-bd-group" open>${renderCrumbBand(g.crumbs)}<div class="ft-bd-group-body">${g.rows}</div></details>`;
    }

    return `
        <div class="list-group list-group-flush border rounded overflow-hidden small text-start w-100 d-flex flex-column align-items-stretch">
            ${flushRows}${bands}
            <div class="list-group-item list-group-item-light d-flex align-items-center justify-content-between gap-2 py-2 w-100 fw-semibold">
                <div class="d-flex align-items-center gap-2 text-start flex-grow-1 min-w-0">
                    <span class="flex-shrink-0 d-inline-flex align-items-center" aria-hidden="true">${iconMap(totalFooterIconSlug, 22, 22)}</span>
                    <span>Total</span>
                </div>
                <div class="d-flex align-items-center justify-content-center flex-shrink-0">
                    <h3 class="mb-0 lh-1 d-flex align-items-center"><span class="badge ${badgeClass}">${rolledTotal}</span></h3>
                </div>
            </div>
        </div>`;
}
